import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Button, Toggle } from '@storable/canaveral-components';
import { FACILITY_LINK, FACILITY_LISTING, BLOG_PAGE, BLOG_CATEGORY_PAGE } from 'shared/pageTypes';
import Authorization from 'containers/Authorization';
import { Link } from 'react-router';
import { BUILDER, SEO_PAGES, DRAFT, FACILITY_PAGES, NON_FACILITY_PAGES } from 'shared/abilities';
import { DEFAULT_LOCALE } from 'shared/i18n';
import Select from '../../../../../../modules/react/components/stateful/Select';
import UnauthorizedBanner from '../UnauthorizedBanner';
import useDebounce from '../../../../../../modules/react/hooks/useDebounce';
import { MANAGE } from '../../../../../../modules/authorization/actions';
import useCurrentUser from '../../../../../../admin/hooks/useCurrentUser';
import PageSettingsLanguages from '../../../../../../admin/containers/PageSettingsLanguages';
import URLDisplay from '../../../../../../admin/containers/urlDisplay';
import SinglePageSettings from '../../containers/SinglePageSettings';
import style from './PageSettings.scss';
import useQuery from '../../../../../../modules/react/hooks/useQuery';

const GET_APPLICABLE_FACILITIES = gql`
query ApplicableFacilities {
  applicableFacilities {
    applicableFeesEnabled
    id
    name
    formattedAddress
  }
}
`;

export const canAccessPage = ({
  canAccessDraft,
  canAccessFacilityPages,
  canAccessNonFacilityPages,
  profile,
  page: {
    facility,
    live,
    pageType,
  },
}) => {
  // links and listings don't have settings
  if (pageType && [FACILITY_LINK, FACILITY_LISTING].indexOf(pageType.code) > -1) {
    return false;
  }

  // don't show draft pages to users wo/that permission
  if (!live && !canAccessDraft) return false;

  // if a facility page, check if they can edit facility pages AND that they can access this specific facility
  if (facility?.id) {
    return canAccessFacilityPages && profile.associatedWithFacility(facility.id);
  }

  // if not a facility page, see if they have access to non-facility pages
  return canAccessNonFacilityPages;
};

const propTypes = {
  pages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    metadata: PropTypes.shape({
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired,
  })),
  pushRoute: PropTypes.func.isRequired,
  pageId: PropTypes.string,
  languages: PropTypes.array,
  hostnames: PropTypes.shape({
    fqdn: PropTypes.string,
  }),
  loadPage: PropTypes.func.isRequired,
  loadParentPage: PropTypes.func.isRequired,
  selectedPage: PropTypes.object,
  parentPage: PropTypes.object,
  loading: PropTypes.bool,
  companyId: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  saveSite: PropTypes.func.isRequired,
  showApplicableFees: PropTypes.bool,
  updateFacility: PropTypes.func,
};

const defaultProps = {
  pageId: null,
  languages: [DEFAULT_LOCALE],
  hostnames: {},
  selectedPage: null,
  parentPage: null,
  loading: true,
};

const PageSettings = ({
  pages,
  pushRoute,
  pageId,
  languages,
  hostnames,
  loadPage,
  loadParentPage,
  selectedPage,
  parentPage,
  loading,
  onSearch,
  saveSite,
  showApplicableFees,
  updateFacility,
}) => {
  const profile = useCurrentUser();
  const [pageValue, setPageValue] = useState('');
  const [searchValue, setValue] = useState('');
  const debouncedValue = useDebounce(searchValue);
  const [authorizedPages, setAuthorizedPages] = useState([]);
  const [showApplicableFee, setShowApplicableFee] = useState(showApplicableFees || showApplicableFees === null);
  const [applicableFacilities, setApplicableFacilities] = useState();
  const [queryLoading, setQueryLoading] = useState();
  const [queryError, setQueryError] = useState();
  const [toggleStates, setToggleStates] = useState({});

  const { data, loading: dataLoading, error } = useQuery(GET_APPLICABLE_FACILITIES, {
    fetchPolicy: 'no-cache',
    ssr: false,
    skip: !showApplicableFee,
  });

  useEffect(() => {
    if (showApplicableFee) {
      if (data) {
        setApplicableFacilities(data.applicableFacilities);
        const initialToggleStates = data.applicableFacilities.reduce((acc, facility) => {
          acc[facility.id] = facility.applicableFeesEnabled === null || facility.applicableFeesEnabled;
          return acc;
        }, {});
        setToggleStates(initialToggleStates);
      } else {
        setApplicableFacilities(null);
      }
      setQueryLoading(dataLoading);
      setQueryError(error);
    }
  }, [loading, data, error]);

  useEffect(() => {
    if (showApplicableFee !== showApplicableFees) {
      saveSite({ showApplicableFees: showApplicableFee });
    }
  }, [showApplicableFee]);

  const handleToggle = () => {
    setShowApplicableFee(prevState => !prevState);
  };


  useEffect(() => {
    if (loading) {
      return;
    }

    onSearch(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    if (pageId) {
      loadPage(pageId);
    }
  }, [pageId]);

  useEffect(() => {
    if (selectedPage) {
      setPageValue(selectedPage);
      if (selectedPage.parent) {
        loadParentPage(selectedPage.parent);
      }
    }
  }, [selectedPage]);

  useEffect(() => {
    if (pages) {
      const canAccessSettings = profile.can(MANAGE, BUILDER, SEO_PAGES);
      const canAccessFacilityPages = profile.can(MANAGE, FACILITY_PAGES);
      const canAccessNonFacilityPages = profile.can(MANAGE, NON_FACILITY_PAGES);
      const canAccessDraft = profile.can(MANAGE, DRAFT);

      const filteredPages = (canAccessFacilityPages || canAccessNonFacilityPages)
        && canAccessSettings ? pages.filter(page => canAccessPage({
          canAccessDraft,
          canAccessFacilityPages,
          canAccessNonFacilityPages,
          profile,
          page,
        })) : [];
      setAuthorizedPages(filteredPages);
    }
  }, [pages]);

  const goToPage = (selectedPageId) => {
    pushRoute(selectedPageId ? `/admin/settings/pages?page=${selectedPageId}` : '/admin/settings/pages');
  };

  const handlePageChange = (page) => {
    if (page) loadPage(page.id);
    goToPage(page ? page.id : null);
  };

  const selectLanguage = (newPageId) => {
    loadPage(newPageId);
    goToPage(newPageId);
  };

  const handleFacilityChange = (facilityId) => {
    setToggleStates((prevState) => {
      const updatedState = { ...prevState, [facilityId]: !prevState[facilityId] };
      updateFacility(facilityId, { applicableFeesEnabled: updatedState[facilityId] });
      return updatedState;
    });
  };

  const renderOption = (page) => {
    let { internalName } = page;
    let { title: pageTitle } = page;
    if (page.metadata) {
      internalName = page.metadata.internalPageName;
      pageTitle = page.metadata.title;
    }

    return (
      <Authorization action="manage" subject={[BUILDER, DRAFT]}>
        {authorized => (
          <div>
            <span>
              {internalName || pageTitle}
            </span>
            {page.facility && (
              <span className={style.pageAddress}>
                - {page.facility.address.fullAddress}
              </span>
            )}
            {authorized && page.live && (
              <span className={style.live}>
                LIVE
              </span>
            )}
          </div>
        )}
      </Authorization>
    );
  };

  if (!pages || !profile) {
    return <div>Loading...</div>;
  }

  const enabledLocales = languages.map(language => language.code);

  if (selectedPage && selectedPage.locale !== DEFAULT_LOCALE && parentPage) {
    selectedPage.metadata.internalPageName = parentPage.metadata.internalPageName;
  }

  return (
    <Authorization action="manage" subject={[SEO_PAGES, BUILDER]}>
      {settingAuthorized => (settingAuthorized ? (
        <div className={style.scrollableContainer}>
          <div className={style.pageSettingsHeader}>
            <span className={style.pageSettings}>
              Page Settings
            </span>
          </div>
          <div className={style.sectionContainer}>
            <div className={style.pageSelectContainer}>
              <div className={style.heading}>
                Page
              </div>
              <div>
                <Select
                  instanceId="page-settings"
                  className={style.select}
                  options={authorizedPages}
                  searchable
                  filterOption={options => options}
                  onInputChange={option => setValue(option)}
                  optionRenderer={page => renderOption(page)}
                  valueRenderer={page => renderOption(page)}
                  onChange={page => handlePageChange(page)}
                  value={pageValue}
                  valueKey="id"
                  onSelectResetsInput={false}
                  placeholder="Select a page from the list or start typing"
                  isLoading={loading}
                />
                {enabledLocales.length > 1 && selectedPage && (
                  <PageSettingsLanguages
                    selectedPageId={selectedPage.id}
                    languages={enabledLocales}
                    selectLanguage={selectLanguage}
                  />
                )}
                {selectedPage && ![BLOG_PAGE, BLOG_CATEGORY_PAGE].includes(selectedPage.metadata.type) && (
                  <React.Fragment>
                    <Button
                      componentClass={Link}
                      to={`/admin/page/edit/${selectedPage.id}`}
                      context="neutral"
                      className={style.editButton}
                    >
                      Edit Page In Builder
                    </Button>
                    <URLDisplay hostnames={hostnames} id={selectedPage.id} />
                  </React.Fragment>
                )}
              </div>
            </div>
            {selectedPage && (
              <SinglePageSettings
                page={selectedPage}
              />
            )}
          </div>
          <div className={style.pageSettingsHeader}>
            <span className={style.pageSettings}>
              Global Settings
            </span>
          </div>
          <div className={style.toggleContainer}>
            <div className={style.toggle}>
              <Toggle
                onClick={handleToggle}
                checked={showApplicableFee}
              />
            </div>
            <div className={style.textContainer}>
              <div className={style.header}>Display Hidden Fees</div>
              <div className={style.description}>
                <span
                  role="img"
                  aria-label="warning"
                  aria-labelledby="warning"
                >⚠️
                </span>
                Disabling this setting may result in non-compliance with applicable laws and regulations.
                Proceeding with this action could expose you to legal risks and potential penalties.
                Please consult with legal counsel before making any changes.
              </div>
            </div>
          </div>
          {showApplicableFee && (
            <>
              {queryLoading && <div className={style.message}>Loading applicable facilities...</div>}
              {queryError &&
              <div className={style.message}>Error fetching applicable facilities. Please try again.
              </div>
              }
              {!queryLoading && !queryError && applicableFacilities && applicableFacilities.length > 0 && (
                <>
                  {applicableFacilities.map(facility => (
                    <div className={style.toggleFacilitiesContainer}>
                      <div key={facility.id} className={style.toggle}>
                        <Toggle
                          onClick={() => handleFacilityChange(facility.id)}
                          checked={toggleStates[facility.id] !== undefined ? toggleStates[facility.id] : false} // Render the correct toggle state
                        />
                      </div>
                      <div className={style.textContainer}>
                        <div className={style.header}>{facility.name}</div>
                        <div className={style.description}>{facility.formattedAddress}</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <UnauthorizedBanner>
          You don't have permission to access this area.  If you wish to do so, please contact support.
        </UnauthorizedBanner>
      )
      )}
    </Authorization>
  );
};

PageSettings.propTypes = propTypes;
PageSettings.defaultProps = defaultProps;

PageSettings.fragments = {
  pagesConnection: gql`
    fragment PageSettingsPageDropdownPagesConnection on PagesConnection {
      edges {
        node {
          id
          live
          locale
          facility {
            id
            address {
              fullAddress
            }
          }
          pageType {
            name
            code
          }
          canonical
          title
          internalName
          siblings {
            id
            locale
          }
        }
      }
      totalCount
    }
  `,
};

export default PageSettings;
